import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'components/Table/Table';
import { useState } from 'react';
import { ACCOUNTING_EXPORTS, EXPORT_ACCOUNTING_DATA } from 'services/accounting';

const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-CH', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

export const AccountingExport = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [exportType, setExportType] = useState('INVOICES');
    const [status, setStatus] = useState('');
    const [invoiceType, setInvoiceType] = useState('');

    const { data: historyData, refetch } = useQuery(ACCOUNTING_EXPORTS, {
        variables: { first: 50 },
    });

    const [exportData, { loading, data: exportResult }] = useMutation(EXPORT_ACCOUNTING_DATA, {
        onCompleted: () => refetch(),
    });

    const exports = historyData?.accountingExports?.edges?.map((e: { node: unknown }) => e.node) || [];

    const handleExport = async () => {
        if (!startDate || !endDate) {
            alert('Please select start and end dates');
            return;
        }

        await exportData({
            variables: {
                startDate,
                endDate,
                exportType,
                status: status || null,
                invoiceType: invoiceType || null,
            },
        });
    };

    return (
        <div>
            <Breadcrumbs crumbs={['Dashboard', 'Accounting', 'Export']} />
            <PageTitle>Accounting Export</PageTitle>

            <Card className="p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Export Invoices</h3>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Start Date *</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">End Date *</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Export Type</label>
                        <select
                            value={exportType}
                            onChange={(e) => setExportType(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        >
                            <option value="INVOICES">Invoice Summaries</option>
                            <option value="INVOICE_LINES">Line Details</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Status (optional)</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        >
                            <option value="">All Statuses</option>
                            <option value="PAID">Paid</option>
                            <option value="SENT">Sent</option>
                            <option value="OVERDUE">Overdue</option>
                            <option value="DRAFT">Draft</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Invoice Type (optional)</label>
                        <select
                            value={invoiceType}
                            onChange={(e) => setInvoiceType(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                        >
                            <option value="">All Types</option>
                            <option value="CONSUMPTION">Consumption</option>
                            <option value="MEMBERSHIP">Membership</option>
                            <option value="CUSTOM">Custom</option>
                        </select>
                    </div>
                </div>

                <Button onClick={handleExport} disabled={loading || !startDate || !endDate}>
                    <Icon name="export" className="mr-2 h-4 w-4" />
                    {loading ? 'Exporting...' : 'Export'}
                </Button>

                {exportResult?.exportAccountingData && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                        <p className="font-medium text-green-800">
                            Export successful! {exportResult.exportAccountingData.recordCount} invoices exported.
                        </p>
                        <a
                            href={exportResult.exportAccountingData.exportUrl}
                            download={exportResult.exportAccountingData.filename}
                            className="text-blue-600 hover:underline inline-flex items-center mt-2"
                        >
                            <Icon name="download" className="mr-1 h-4 w-4" />
                            Download {exportResult.exportAccountingData.filename}
                        </a>
                    </div>
                )}
            </Card>

            <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Export History</h3>
                <Table className="border">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Date Range</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Filters</TableHead>
                            <TableHead>Records</TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead>Created By</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead>Download</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {exports.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                                    No exports yet
                                </TableCell>
                            </TableRow>
                        )}
                        {exports.map((exp: {
                            id: string;
                            startDate: string;
                            endDate: string;
                            exportType: string;
                            statusFilter: string;
                            typeFilter: string;
                            recordCount: number;
                            provider: string;
                            createdBy?: { firstName: string; lastName: string };
                            createdAt: string;
                            fileUrl: string;
                        }) => (
                            <TableRow key={exp.id}>
                                <TableCell>
                                    {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {exp.exportType === 'INVOICE_LINES' ? 'Lines' : 'Summaries'}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm text-gray-600">
                                        {exp.statusFilter && <span>Status: {exp.statusFilter}</span>}
                                        {exp.statusFilter && exp.typeFilter && ', '}
                                        {exp.typeFilter && <span>Type: {exp.typeFilter}</span>}
                                        {!exp.statusFilter && !exp.typeFilter && <span>-</span>}
                                    </div>
                                </TableCell>
                                <TableCell>{exp.recordCount}</TableCell>
                                <TableCell>
                                    <Badge variant="secondary">{exp.provider.toUpperCase()}</Badge>
                                </TableCell>
                                <TableCell>
                                    {exp.createdBy
                                        ? `${exp.createdBy.firstName} ${exp.createdBy.lastName}`
                                        : '-'}
                                </TableCell>
                                <TableCell>{formatDate(exp.createdAt)}</TableCell>
                                <TableCell>
                                    {exp.fileUrl && (
                                        <a
                                            href={exp.fileUrl}
                                            download
                                            className="text-blue-600 hover:underline inline-flex items-center"
                                        >
                                            <Icon name="download" className="h-4 w-4" />
                                        </a>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
};
